import { Box, Flex } from '@chakra-ui/react';
import { GetServerSideProps, NextPage } from 'next';

import { EventsPageSeo } from '@/components/ui/features/events-app/event/EventsPageSeo/EventsPageSeo';
import { PostersHeaderDesktop } from '@/components/ui/features/posters/PostersHeader/PostersHeaderDesktop';
import { PostersHeaderMobile } from '@/components/ui/features/posters/PostersHeader/PostersHeaderMobile';
import { SearchQuery } from '@/components/ui/features/schedule/ScheduleQuerySearch/SearchQuery';
import { EventAppLayout } from '@/components/ui/shared/layouts/EventAppLayout/EventAppLayout';
import { NoTicketCheck } from '@/components/ui/shared/overlays/NoTicketCheck/NoTicketCheck';
import { EventSessions } from '@/containers/Event/EventSessions';
import { SearchCategories, SearchGroup, SearchQueryScope } from '@/enums/index';
import { useQueryParams } from '@/hooks/useQueryParams';
import { queryThinEvent } from '@/queries/thin-events';
import { generateStore } from '@/utils/generateStore';
import { Hydrate, IHydrateProps } from '@/utils/swr/Hydrate';
import { StringParam, urlQueryStringValidate } from '@/utils/url-query-string';

const EventPostersPage: NextPage<IHydrateProps> = ({ fallback }) => {
  const { eventId, query, searchGroup } = useQueryParams<{ eventId: string; query: string; searchGroup: string }>({
    eventId: StringParam,
    query: StringParam,
    searchGroup: StringParam,
  });

  const isSessions = searchGroup === SearchGroup.EVENT_SESSION;

  return (
    <Hydrate fallback={fallback}>
      <EventAppLayout>
        <EventsPageSeo page="posters" />
        <NoTicketCheck eventId={eventId}>
          <Flex
            backgroundColor="white"
            flexDir={{ base: 'column', lg: 'row' }}
            alignItems="center"
            justifyContent="flex-end"
            py={4}
            px={{ base: 4, sm: 8 }}
            position="sticky"
            top="4.5rem"
            zIndex={20}
            borderBottomWidth="1px"
            borderBottom="gray.200"
          >
            <PostersHeaderMobile />
            <PostersHeaderDesktop />
          </Flex>

          {!isSessions && query ? (
            <Box bg="gray.50">
              <SearchQuery scope={SearchQueryScope.POSTERS} />
            </Box>
          ) : (
            <EventSessions scope={SearchCategories.POSTERS} />
          )}
        </NoTicketCheck>
      </EventAppLayout>
    </Hydrate>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  const { eventId, eventSessionId } = urlQueryStringValidate<{ eventId: string; eventSessionId: string }>(
    context.query,
    {
      eventId: StringParam,
      eventSessionId: StringParam,
    }
  );

  // poster sessions used to be shown here via ?eventSessionId=; keep old links alive
  if (eventSessionId) {
    return {
      redirect: {
        destination: `/events/${eventId}/posters/${eventSessionId}`,
        permanent: false,
      },
    };
  }

  try {
    await Promise.all([store.fetchResource(queryThinEvent(eventId))]);

    return {
      props: {
        fallback: store.fallback,
      },
    };
  } catch (err) {
    return {
      props: {
        fallback: store.fallback,
      },
    };
  }
};

export default EventPostersPage;
